import {
	closeDrawer,
	isOpenInfoModal,
	isReportDrawerOpen,
	reportFormDataInnit,
	reportListData,
	selectedInfoModalTab,
} from 'Pages/ReportsAlerts/Reports/@signals'
import { INFO_MODAL_TABS } from 'Pages/ReportsAlerts/Reports/@constants'

// ─── DOM helpers ────────────────────────────────────────────────────────────

const q = id => document.querySelector(`[data-tour-id="${id}"]`)

// ─── Selection bar helpers ──────────────────────────────────────────────────
// The tour ticks the first row (real table or demo) so the floating bar appears, and clears that selection
// when the tour ends.
let tourSelectedRow = false

function selectFirstRow() {
	if (q('tour-multi-btn-edit')) return
	const cb = document.querySelector('tbody td .MuiCheckbox-root input[type="checkbox"]')
	if (cb) {
		cb.click()
		tourSelectedRow = true
	}
}

const isEditMenuOpen = () => !!q('tour-multi-btn-sites')

function openEditMenu() {
	const btn = q('tour-multi-btn-edit')
	if (btn && btn.getAttribute('aria-expanded') !== 'true') btn.click()
	// the menu focuses its first item: take the focus off it, so Enter moves the tour and never runs the item
	setTimeout(() => document.activeElement?.blur?.(), 80)
}

function closeEditMenu() {
	q('tour-multi-btn-sites')?.closest('.MuiPopover-root')?.querySelector('.MuiBackdrop-root')?.click()
}

function clearTourSelection() {
	closeEditMenu()
	if (!tourSelectedRow) return
	tourSelectedRow = false
	document.querySelector('[data-tour-id="tour-multi-bar"] [aria-label="Clear selection"]')?.click()
}

// A button of the selection bar, with the Edit menu closed so it does not hide the bar
function barButton(id) {
	if (isEditMenuOpen()) closeEditMenu()
	return q(id)
}

function findByText(text, exact = false) {
	const els = document.querySelectorAll('span, p, label, h6, h5, h4, .MuiTypography-root')
	for (const el of els) {
		const content = el.textContent.trim().toLowerCase()
		if (exact ? content === text.toLowerCase() : content.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function findByTooltip(text) {
	const els = document.querySelectorAll('[aria-label], [title]')
	for (const el of els) {
		const label = (el.getAttribute('aria-label') || el.getAttribute('title') || '').toLowerCase()
		if (label.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function findTableColumn(headerText, exact = false) {
	const headers = document.querySelectorAll('th, [class*="MUIDataTableHeadCell"]')
	for (const h of headers) {
		const content = h.textContent.trim().toLowerCase()
		if (exact ? content === headerText.toLowerCase() : content.includes(headerText.toLowerCase())) {
			return h
		}
	}
	return null
}

// Find a section header (the form section card title) scoped to the open drawer
function findSectionDivider(text) {
	const drawer = document.querySelector('.MuiDrawer-paper')
	const scope = drawer || document
	const els = scope.querySelectorAll('section h3, .MuiTypography-h5')
	for (const el of els) {
		if (el.textContent.trim().toLowerCase().includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

// Find a form field by its MUI InputLabel text, scoped to the open drawer.
// AutocompleteAjaxSeeker / TextField render the label as <label class="MuiInputLabel-root">.
function findDrawerField(labelText) {
	const drawer = document.querySelector('.MuiDrawer-paper')
	const scope = drawer || document
	const labels = scope.querySelectorAll('label.MuiInputLabel-root, label.MuiFormLabel-root')
	for (const label of labels) {
		if (label.textContent.trim().toLowerCase().includes(labelText.toLowerCase())) {
			return label.closest('.MuiFormControl-root') || label
		}
	}
	return null
}

// Find an AppAntSwitch by its trueText.
// AntSwitchSeeker renders trueText as <Typography> (p.MuiTypography-root) inside a MuiStack-root.
function findDrawerSwitch(trueText) {
	const drawer = document.querySelector('.MuiDrawer-paper')
	const scope = drawer || document
	const els = scope.querySelectorAll('.MuiTypography-root')
	for (const el of els) {
		if (el.textContent.trim().toLowerCase() === trueText.toLowerCase()) {
			return el.closest('.MuiStack-root') || el.parentElement
		}
	}
	return null
}

// ─── Language detection ──────────────────────────────────────────────────────

const ES = navigator.language?.startsWith('es')

// Close drawer on tour skip/close during drawer section
const drawerCleanup = () => closeDrawer()

// ─── Steps ───────────────────────────────────────────────────────────────────

export const TOUR_STEPS = [
	// ─── 0: Intro ───────────────────────────────────────────────────────────────
	{
		type: 'intro',
		icon: 'file-lines',
		title: ES ? 'Bienvenido al Gestor de Informes' : 'Welcome to the Reports Manager',
		description: ES
			? 'El Gestor de Informes es el centro de control para crear, programar y supervisar tus informes de busqueda de precios. Desde aqui controlas toda la recopilacion automatizada de datos en sitios, oficinas y proveedores.'
			: 'The Reports Manager is the central hub for creating, scheduling, and monitoring your pricing search reports. From here you control all automated data collection across sites, offices, and providers.',
	},
	// ─── 1: Icons Legend ────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const legend = document.querySelector('[data-tour-id=\"tour-icons-legend\"]')
			if (legend) return legend
			const el = findByText('Download Report')
			if (!el) return null
			let parent = el.parentElement
			for (let i = 0; i < 6; i++) {
				if (!parent) break
				const text = parent.textContent.toLowerCase()
				if (text.includes('download report') && text.includes('download export')) {
					if (parent.children.length >= 3) return parent
				}
				parent = parent.parentElement
			}
			return el.parentElement
		},
		icon: 'icons',
		title: ES ? 'Leyenda de Iconos' : 'Icons Legend',
		description: ES
			? 'Esta leyenda explica los iconos utilizados en la tabla de informes. Cada icono representa una accion rapida: abrir un analizador (LOR, DOR, Brokers, Sitio Directo, Valoracion) o descargar un informe/exportacion.'
			: 'This legend explains the icons used throughout the reports table. Each icon represents a quick action: open an analyzer (LOR, DOR, Brokers, Direct Site, Rating) or download a report/export.',
		example: ES
			? 'LOR | DOR | Brokers | Sitio Directo | Valoracion | Descargar Informe | Descargar Exportacion'
			: 'LOR | DOR | Brokers | Direct Site | Rating | Download Report | Download Export',
	},
	// ─── 2: Create Report — opens the drawer ────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			if (q('tour-create-report')) return q('tour-create-report')
			const btn = findByText('Create Report')
			return btn?.closest('button') || btn?.parentElement
		},
		icon: 'file-plus',
		title: ES ? 'Crear Informe' : 'Create Report',
		description: ES
			? 'Este boton abre el formulario de creacion. Pulsa «Next» y lo abriremos para recorrer cada campo juntos: nombre, sitios, oficinas, fechas de busqueda, filtros, alertas y frecuencia de ejecucion.'
			: 'This button opens the creation form. Press "Next" and we will open it and walk through each field together: name, sites, offices, search dates, filters, alerts, and execution frequency.',
		action: () => reportFormDataInnit(),
		waitFor: () => isReportDrawerOpen.value === true,
	},
	// ─── 3: Drawer overview ─────────────────────────────────────────────────────
	{
		id: 'drawer-start',
		type: 'intro',
		icon: 'rectangle-list',
		title: ES ? 'Formulario de Creacion' : 'Creation Form',
		description: ES
			? 'El formulario tiene seis secciones: Nombre, Sitios y Oficinas, Configuracion de Busqueda, Filtros de Exportacion, Alertas y Frecuencia. Vamos campo por campo.'
			: 'The form has six sections: Name, Sites & Offices, Search Configuration, Export Filters, Alerts, and Frequency. Let\'s go field by field.',
		reverseAction: () => closeDrawer(),
		cleanup: drawerCleanup,
	},
	// ─── 4: Num. Searches info button ────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-num-searches-info"]'),
		icon: 'circle-info',
		title: ES ? 'Informacion de Busquedas' : 'Search Info',
		description: ES
			? 'El indicador "Num. Searches" muestra el total de busquedas que ejecutara este informe. Pulsa el icono para ver el desglose detallado: numero de oficinas, busquedas por oficina, fechas de recogida primera y ultima, y el listado completo de fechas de pickup.'
			: 'The "Num. Searches" indicator shows the total searches this report will execute. Click the info icon to see the detailed breakdown: number of offices, searches per office, first and last pickup dates, and the full pickup date list.',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 1 — Report Name
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 5: Report Name field ───────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-report-name"]'),
		icon: 'pen-line',
		title: ES ? 'Nombre del Informe' : 'Report Name',
		description: ES
			? 'El nombre que identifica este informe en la lista. Usa un nombre descriptivo que incluya el mercado o sitio, por ejemplo "Spain - Malaga Weekly". Minimo 5 caracteres, maximo 100.'
			: 'The name that identifies this report in the list. Use a descriptive name that includes the market or site, e.g. "Spain - Malaga Weekly". Minimum 5 characters, maximum 100.',
		example: ES ? 'Ej: Spain - Malaga Airport Weekly' : 'E.g.: Spain - Malaga Airport Weekly',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 2 — Sites & Offices
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 6: Use Office Group switch ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-office-switch"]'),
		icon: 'toggle-off',
		title: ES ? 'Modo de Oficina' : 'Office Mode',
		description: ES
			? 'Elige como seleccionar las oficinas. «Offices»: seleccionas oficinas de recogida individuales. «Office group»: usas un grupo de oficinas predefinido que cubre varias ubicaciones a la vez. Los grupos son mas practicos para monitorizar mercados enteros.'
			: 'Choose how to select offices. "Offices": pick individual pickup offices. "Office group": use a predefined office group that covers multiple locations at once. Groups are more practical for monitoring entire markets.',
		example: ES ? 'Office group = Grupo | Offices = Oficinas individuales' : 'Office group = Group | Offices = Individual offices',
		cleanup: drawerCleanup,
	},
	// ─── 7: Pickup Office / Group Office ────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-office-field"]'),
		icon: 'building',
		title: ES ? 'Oficinas de Recogida' : 'Pickup Offices',
		description: ES
			? 'Las oficinas (o el grupo) que quieres monitorizar. Puedes seleccionar varias oficinas individuales a la vez. El numero de oficinas multiplicado por los sitios y las fechas determina el total de busquedas del informe.'
			: 'The offices (or group) you want to monitor. You can select multiple individual offices at once. The number of offices multiplied by sites and dates determines the total searches for the report.',
		cleanup: drawerCleanup,
	},
	// ─── 8: Sites ───────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-site"]'),
		icon: 'globe',
		title: ES ? 'Sitios Web' : 'Sites',
		description: ES
			? 'Las webs de alquiler de coches que quieres monitorizar: OTAs (Booking, Rentalcars...), brokers o el sitio directo. Puedes seleccionar varios sitios — cada uno genera sus propias busquedas de precio.'
			: 'The car rental websites you want to monitor: OTAs (Booking, Rentalcars...), brokers, or the direct site. You can select multiple sites — each one generates its own price searches.',
		cleanup: drawerCleanup,
	},
	// ─── 9: Currency ────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-currency"]'),
		icon: 'euro-sign',
		title: ES ? 'Moneda' : 'Currency',
		description: ES
			? 'La moneda en la que se mostraran los precios en los resultados del informe. Selecciona la moneda que corresponda al mercado que estas monitorizando.'
			: 'The currency in which prices will be shown in the report results. Select the currency that matches the market you are monitoring.',
		cleanup: drawerCleanup,
	},
	// ─── 10: More Features / Dropoff switch ──────────────────────────────────────
	{
		id: 'dropoff',
		type: 'field',
		// the "More options" link while it is closed, the whole block once it shows the Dropoff Office field
		findTarget: () => q('tour-dropoff')?.querySelector('[aria-expanded="false"]') || q('tour-dropoff'),
		icon: 'right-left',
		title: ES ? 'Oficina de Devolucion (Ruta de Solo Ida)' : 'Dropoff Office (One-Way Route)',
		description: ES
			? 'Abre «More options» si la oficina de devolucion es diferente a la de recogida. Util para rutas aeropuerto→ciudad o entre aeropuertos. Solo funciona con una unica oficina de recogida seleccionada (no con grupos ni multiples oficinas).'
			: 'Open "More options" if the dropoff office differs from the pickup. Useful for airport→city routes or between airports. Only works with a single pickup office selected (not with groups or multiple offices).',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 3 — Search Configuration
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 11: Use CSV switch ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-csv-switch"]'),
		icon: 'file-csv',
		title: ES ? 'Modo de Busqueda: Fecha o CSV' : 'Search Mode: Date or CSV',
		description: ES
			? 'Con «Date setup» (por defecto) el informe genera busquedas a partir de la configuracion de fechas. Si eliges «CSV file», puedes subir un fichero con busquedas completamente personalizadas — util para casos especificos que no caben en la configuracion estandar.'
			: 'With "Date setup" (the default) the report generates searches from the date configuration. If you choose "CSV file", you can upload a file with fully custom searches — useful for specific cases that do not fit the standard configuration.',
		cleanup: drawerCleanup,
	},
	// ─── 12: Pickup Dates mode selector ─────────────────────────────────────────
	{
		type: 'field',
		// the mode selector (first field of the row); the row itself as a fallback
		findTarget: () => q('tour-pickup-dates')?.querySelector('.MuiFormControl-root') || q('tour-pickup-dates'),
		icon: 'calendar-range',
		title: ES ? 'Modo de Fechas de Recogida' : 'Pickup Dates Mode',
		description: ES
			? 'Define como se seleccionan las fechas de recogida. Cuatro opciones: dias especificos (fechas concretas), lead times (x dias desde hoy), dia y semana (p.ej. lunes de la 2a semana) o proximo mes completo. Cada modo genera un subformulario diferente.'
			: 'Defines how pickup dates are selected. Four options: specific days (exact dates), lead times (x days from today), day and week (e.g. Monday of 2nd week), or next full month. Each mode shows a different sub-form.',
		example: ES
			? 'Lead times: 1,3,7,14,30 dias | Especificos: 15/06, 22/06...'
			: 'Lead times: 1,3,7,14,30 days | Specific: 15/06, 22/06...',
		cleanup: drawerCleanup,
	},
	// ─── 13: Rental length ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-rental-length"]'),
		icon: 'moon',
		title: ES ? 'Duracion del Alquiler' : 'Rental Length',
		description: ES
			? 'El numero de noches del alquiler que se buscaran. Puedes seleccionar varias duraciones a la vez — por ejemplo, 1, 3, 7 noches. Cada duracion combinada con cada fecha genera una busqueda independiente.'
			: 'The number of rental nights to search. You can select multiple durations at once — e.g. 1, 3, 7 nights. Each duration combined with each date generates an independent search.',
		example: ES ? 'Ej: 1, 3, 5, 7 noches → 4x busquedas por fecha' : 'E.g.: 1, 3, 5, 7 nights → 4x searches per date',
		cleanup: drawerCleanup,
	},
	// ─── 14: Days to monitor ────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-days-monitor"]'),
		icon: 'calendar-days',
		title: ES ? 'Dias a Monitorizar' : 'Days to Monitor',
		description: ES
			? 'Cuantos dias consecutivos a partir de hoy incluira el informe. Por ejemplo, 30 dias significa que se buscaran precios para los proximos 30 dias de recogida (segun el modo de fechas elegido). Maximo 365 dias.'
			: 'How many consecutive days from today the report will include. For example, 30 days means prices will be searched for the next 30 pickup days (according to the chosen date mode). Maximum 365 days.',
		cleanup: drawerCleanup,
	},
	// ─── 15: Pickup Time / Dropoff Time ─────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-pickup-time"]'),
		icon: 'clock',
		title: ES ? 'Hora de Recogida' : 'Pickup Time',
		description: ES
			? 'La hora de recogida («Pickup Time») que se usara en las busquedas. Algunos sitios muestran precios diferentes segun la hora, por lo que es importante configurarla correctamente para obtener precios comparables.'
			: 'The pickup time ("Pickup Time") used in searches. Some sites show different prices depending on the time, so it is important to configure it correctly for comparable prices.',
		example: ES ? 'Recogida: 10:00' : 'Pickup: 10:00',
		cleanup: drawerCleanup,
	},
	// ─── 15b: Dropoff Time ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => q('tour-dropoff-time'),
		icon: 'clock',
		title: ES ? 'Hora de Devolucion' : 'Dropoff Time',
		description: ES
			? 'La hora de devolucion («Dropoff Time») de las busquedas. Junto con la hora de recogida y la duracion define el alquiler exacto que se busca en cada sitio.'
			: 'The dropoff time ("Dropoff Time") of the searches. Together with the pickup time and the rental length it defines the exact rental searched on every site.',
		example: ES ? 'Devolucion: 10:00' : 'Dropoff: 10:00',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 4 — Export Filters (optional)
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 16: Export Filters section ─────────────────────────────────────────────
	{
		id: 'export-filters',
		type: 'field',
		// the section's Hide / Show selector: the section is closed until it says Show
		findTarget: () => q('tour-export-filters')?.querySelector('[aria-label="Section"]') || q('tour-export-filters'),
		icon: 'sliders',
		title: ES ? 'Filtros de Exportacion (Opcional)' : 'Export Filters (Optional)',
		description: ES
			? 'Seccion opcional — elige «Show» en este selector para ver los campos. Permite filtrar los resultados del Excel por: proveedor, web supplier, politica de combustible, prepago, categoria de vehiculo y codigo ACRISS. Tambien puedes establecer un limite de horas y activar la generacion automatica.'
			: 'Optional section — choose "Show" in this selector to see the fields. Allows filtering Excel results by: provider, web supplier, fuel policy, prepaid, vehicle category, and ACRISS code. You can also set an hours limit and enable automatic generation.',
		example: ES ? 'Proveedor: EuroDrive | Combustible: Full-Full | ACRISS: ECMR' : 'Provider: EuroDrive | Fuel: Full-Full | ACRISS: ECMR',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 5 — Alerts Configuration (optional)
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 17: Alerts Configuration section ───────────────────────────────────────
	{
		id: 'alerts',
		type: 'field',
		findTarget: () => q('tour-alerts')?.querySelector('[aria-label="Section"]') || q('tour-alerts'),
		icon: 'cowbell',
		title: ES ? 'Configuracion de Alertas (Opcional)' : 'Alerts Configuration (Optional)',
		description: ES
			? 'Seccion opcional — elige «Show» para configurar alertas por email. Cinco tipos de alerta disponibles: cambio de precio, precio fuera de rango min/max, comparacion de precios entre sitios, no disponibilidad y cambio en la valoracion. Se envian automaticamente cuando se cumplen las condiciones.'
			: 'Optional section — choose "Show" to configure email alerts. Five alert types available: price change, price out of min/max range, price comparison between sites, unavailability, and rating change. Sent automatically when conditions are met.',
		example: ES ? 'Precio sube >10% | No disponible | Valoracion baja de 8' : 'Price rises >10% | Unavailable | Rating drops below 8',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 6 — Frequency & Notification
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 18: Email ──────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-email"]'),
		icon: 'envelope',
		title: ES ? 'Email de Notificacion' : 'Notification Email',
		description: ES
			? 'Los correos electronicos a los que se enviaran los resultados del informe cuando se complete la ejecucion. Puedes anadir varios emails — pulsa Enter o escribe una coma entre cada uno. Por defecto se rellena con tu email de usuario.'
			: 'The email addresses that will receive report results when execution completes. You can add multiple emails — press Enter or type a comma between each. Defaults to your user email.',
		cleanup: drawerCleanup,
	},
	// ─── 19: Execution frequency (hours) ────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-exec-frequency"]'),
		icon: 'clock-rotate-left',
		title: ES ? 'Horas de Ejecucion' : 'Execution Hours',
		description: ES
			? 'Las horas del dia en las que se ejecutara automaticamente el informe. Puedes seleccionar varias horas para tener varias "fotos" de precios al dia. Queda desactivado si usas el modo de Ejecucion Manual.'
			: 'The times of day when the report will run automatically. You can select multiple hours to have several daily price snapshots. Disabled if you use Manual Execution mode.',
		example: ES ? 'Ej: 08:00, 12:00, 18:00 → tres capturas al dia' : 'E.g.: 08:00, 12:00, 18:00 → three daily snapshots',
		cleanup: drawerCleanup,
	},
	// ─── 20: Frequencies (days) + close drawer ───────────────────────────────────
	{
		id: 'drawer-end',
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-frequencies"]'),
		icon: 'calendar-week',
		title: ES ? 'Dias de Ejecucion' : 'Execution Days',
		description: ES
			? 'Los dias de la semana en los que se ejecutara el informe. Combinado con las horas de ejecucion define el calendario completo. Cuando lo tengas todo configurado, guarda con el boton "Save" del pie del formulario.'
			: 'The days of the week when the report will run. Combined with execution hours this defines the complete schedule. When everything is configured, save using the "Save" button at the bottom of the form.',
		example: ES ? 'Lunes, Miercoles, Viernes' : 'Monday, Wednesday, Friday',
		action: () => closeDrawer(),
		waitFor: () => !isReportDrawerOpen.value,
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// TABLE SECTION
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 21: Reports Table ──────────────────────────────────────────────────────
	{
		type: 'field',
		// the table card (real or demo), found from its status selector
		findTarget: () => q('tour-status-toggle')?.closest('.MuiPaper-root') || document.querySelector('.MuiTable-root')?.closest('.MuiPaper-root'),
		icon: 'table-cells',
		title: ES ? 'Tabla de Informes' : 'Reports Table',
		description: ES
			? 'La tabla principal con todos tus informes. Puedes ordenar, filtrar, buscar y seleccionar varios informes. Cada fila es un informe con su resumen de configuracion y estado actual.'
			: 'The main table listing all your reports. You can sort, filter, search, and select multiple reports. Each row is one report with its configuration summary and current status.',
	},
	// ─── 21b: Enabled / Disabled / All selector ─────────────────────────────────
	{
		type: 'field',
		findTarget: () => q('tour-status-toggle'),
		icon: 'filter',
		title: ES ? 'Informes Mostrados' : 'Reports Shown',
		description: ES
			? 'Elige que informes lista la tabla: «Enabled» (activos), «Disabled» (desactivados) o «All» (todos). Por defecto ves los activos.'
			: 'Choose which reports the table lists: "Enabled", "Disabled" or "All". By default you see the enabled ones.',
		example: ES ? 'Enabled | Disabled | All' : 'Enabled | Disabled | All',
	},
	// ─── 22: Report Name column ─────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Report name'),
		icon: 'tag',
		title: ES ? 'Nombre del Informe' : 'Report Name',
		description: ES
			? 'El nombre que asignaste a este informe. Usa nombres descriptivos que incluyan el sitio, mercado o proposito para identificar rapidamente los informes en la lista.'
			: 'The name you assigned to this report. Use descriptive names that include the site, market, or purpose so you can quickly identify reports in the list.',
	},
	// ─── 23: Search numbers column ──────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Search numbers') || findTableColumn('Searches'),
		icon: 'hashtag',
		title: ES ? 'Numero de Busquedas' : 'Search Numbers',
		description: ES
			? 'Numero total de busquedas de precios individuales que ejecutara este informe. Los informes con mas de 1.000 busquedas muestran un icono de fuego como advertencia — un alto numero de busquedas tarda mas en completarse y consume mas recursos.'
			: 'Total number of individual price searches this report will execute. Reports with 1,000+ searches display a fire icon as a warning — high search counts take longer to complete and consume more resources.',
		example: ES ? 'Menos de 1K = Normal | 1K+ = Advertencia de fuego' : 'Under 1K = Normal | 1K+ = Fire warning',
	},
	// ─── 24: Sites column ───────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Sites'),
		icon: 'globe',
		title: ES ? 'Sitios' : 'Sites',
		description: ES
			? 'Los sitios web de alquiler de coches que monitoriza este informe. Los informes pueden dirigirse a varios sitios simultaneamente — cada sitio aporta datos de precios de esa OTA, broker o web directa especifica.'
			: 'The rental car websites this report monitors. Reports can target multiple sites simultaneously — each site adds price data from that specific OTA, broker, or direct website.',
	},
	// ─── 25: Frequency Hours column ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Frequency Hours') || findTableColumn('Hours'),
		icon: 'clock',
		title: ES ? 'Horas de Frecuencia' : 'Frequency Hours',
		description: ES
			? 'Las horas del dia en las que se ejecuta este informe. Se pueden configurar varias horas para capturar cambios de precio a lo largo del dia, ayudandote a rastrear patrones de precios intradía.'
			: 'The specific times of day when this report runs. Multiple hours can be set to capture price changes throughout the day, helping you track intraday pricing patterns.',
		example: ES ? '08:00, 12:00, 18:00 — Tres comprobaciones diarias' : '08:00, 12:00, 18:00 — Three daily checks',
	},
	// ─── 26: Frequency Days column ──────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Frequency', true),
		icon: 'calendar-days',
		title: ES ? 'Dias de Frecuencia' : 'Frequency Days',
		description: ES
			? 'Los dias de la semana en los que este informe esta programado para ejecutarse. Combinado con las horas de frecuencia, esto define el calendario de ejecucion completo.'
			: 'The days of the week when this report is scheduled to execute. Combined with frequency hours, this defines the complete execution schedule.',
		example: ES ? 'Lunes, Miercoles, Viernes' : 'Mondays, Wednesdays, Fridays',
	},
	// ─── 27: Pick up & Drop off column ──────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Pick up') || findTableColumn('Airports'),
		icon: 'location-dot',
		title: ES ? 'Recogida y Devolucion' : 'Pick up & Drop off',
		description: ES
			? 'Las oficinas de alquiler monitorizadas por este informe. Muestra el grupo de oficinas o las ubicaciones de recogida especificas. Si la recogida y la devolucion difieren, ambas se muestran para la monitorizacion de rutas de solo ida.'
			: 'The rental offices monitored by this report. Shows the office group or specific pickup locations. If pickup and dropoff differ, both are displayed for one-way route monitoring.',
	},
	// ─── 28: Date column — status overview ──────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const cells = document.querySelectorAll('td')
			for (const cell of cells) {
				if (
					cell.querySelector('[title="Download Report"]') ||
					cell.querySelector('[title="LOR Analyzer"]')
				) return cell
			}
			return findTableColumn('Date')
		},
		icon: 'calendar-check',
		title: ES ? 'Fecha y Estado de Ejecucion' : 'Date & Execution Status',
		description: ES
			? 'La columna Fecha muestra la ultima ejecucion y el estado del informe. Tres estados posibles: Listo (la ejecucion se completo correctamente, con botones de accion disponibles), En ejecucion (el informe esta procesando busquedas, con barra de progreso) y Detenido (la ejecucion fue interrumpida). Haz clic en la barra de progreso para ver el detalle de las busquedas en tiempo real.'
			: 'The Date column shows the last execution and report status. Three possible states: Ready (execution completed successfully, action buttons available), Running (report is processing searches, with progress bar), and Stopped (execution was interrupted). Click the progress bar to see real-time search details.',
		example: ES
			? 'Listo = Botones de accion | En ejecucion = Barra de progreso | Detenido = Interrumpido'
			: 'Ready = Action buttons | Running = Progress bar | Stopped = Interrupted',
	},
	// ─── 29: Date column — running block ────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => (
			document.querySelector('[data-tour-id="tour-running-block"]') ||
			document.querySelector('.MuiLinearProgress-root') ||
			document.querySelector('[role="progressbar"]')
		),
		skipIf: () => !(
			document.querySelector('[data-tour-id="tour-running-block"]') ||
			document.querySelector('.MuiLinearProgress-root')
		),
		icon: 'spinner',
		title: ES ? 'Informe en Ejecución' : 'Report Running',
		description: ES
			? 'Cuando un informe está procesando búsquedas aparece la barra de progreso con el contador de tareas completadas / totales. Haz clic en la barra para abrir el «Search summary»: dos tarjetas, «By website» y «By office», cada una con su porcentaje total y una barra con el porcentaje de cada sitio u oficina. Se actualiza cada 5 segundos mientras se ejecuta.'
			: 'While a report is processing searches, a progress bar appears showing completed / total tasks. Click the bar to open the "Search summary": two cards, "By website" and "By office", each with its overall percentage and one bar with the percentage of every site or office. It updates every 5 seconds while it runs.',
		example: ES
			? '838 / 1250 completadas — haz clic en la barra para ver el avance por sitio y por oficina'
			: '838 / 1250 completed — click the bar to see the progress by website and by office',
	},
	// ─── 30: Date column — LOR Analyzer button ────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-lor"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-lor"]'),
		icon: 'tally',
		title: ES ? 'LOR Analyzer' : 'LOR Analyzer',
		description: ES
			? 'Abre el analizador LOR (Length of Rental — Duracion del Alquiler) con los datos de esta ejecucion ya cargados. Muestra la tabla de precios comparativa entre proveedores para todas las duraciones de alquiler monitorizadas.'
			: 'Opens the LOR Analyzer (Length of Rental) preloaded with data from this execution. Shows the comparative price table across providers for all monitored rental lengths.',
	},
	// ─── 31: Date column — DOR Analyzer button ────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-dor"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-dor"]'),
		icon: 'calendar-days',
		title: ES ? 'DOR Analyzer' : 'DOR Analyzer',
		description: ES
			? 'Abre el analizador DOR (Direct Operating Rate — Tasa Operativa Directa) con los resultados de esta ejecucion. Permite comparar precios por fecha de recogida entre los proveedores y sitios monitorizados.'
			: 'Opens the DOR Analyzer (Direct Operating Rate) with results from this execution. Lets you compare prices by pickup date across monitored providers and sites.',
	},
	// ─── 32: Date column — Brokers Analyzer button ──────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-brokers"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-brokers"]'),
		icon: 'handshake',
		title: ES ? 'Brokers Analyzer' : 'Brokers Analyzer',
		description: ES
			? 'Abre el analizador de Brokers con los datos de esta ejecucion. Muestra los precios de los sitios de competencia (OTAs, brokers) organizados por duracion de alquiler y fecha.'
			: 'Opens the Brokers Analyzer with data from this execution. Shows prices from competition sites (OTAs, brokers) organized by rental length and date.',
	},
	// ─── 33: Date column — Direct Site Analyzer button ──────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-direct"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-direct"]'),
		icon: 'chart-line',
		title: ES ? 'Direct Site Analyzer' : 'Direct Site Analyzer',
		description: ES
			? 'Abre el analizador de Sitio Directo con los datos de esta ejecucion. Permite analizar y comparar los precios publicados en tu web directa frente a los de los competidores.'
			: 'Opens the Direct Site Analyzer with data from this execution. Lets you analyze and compare prices published on your direct website against competitors.',
	},
	// ─── 34: Date column — Rating Analyzer button ───────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-rating"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-rating"]'),
		icon: 'star',
		title: ES ? 'Rating Analyzer' : 'Rating Analyzer',
		description: ES
			? 'Abre el analizador de Valoracion con los datos de esta ejecucion. Muestra las puntuaciones y reviews de los sitios monitorizados para comparar la reputacion online de cada proveedor.'
			: 'Opens the Rating Analyzer with data from this execution. Shows scores and reviews from monitored sites to compare the online reputation of each provider.',
	},
	// ─── 35: Date column — Real-Time Historical button ──────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-realtime"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-realtime"]'),
		icon: 'clock-rotate-left',
		title: ES ? 'Ver en Historico en Tiempo Real' : 'View in Real-Time Historical',
		description: ES
			? 'Abre el historico en tiempo real con los datos de esta ejecucion precargados. Permite navegar por el historial completo de busquedas de precio de esta ejecucion con filtros detallados y graficos de evolucion.'
			: 'Opens the real-time historical view preloaded with data from this execution. Lets you navigate the complete price search history for this execution with detailed filters and evolution charts.',
	},
	// ─── 36: Date column — Categories Matches button ────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-categories"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-categories"]'),
		icon: 'file-excel',
		title: ES ? 'Descargar Coincidencias de Categorias' : 'Get Custom Categories Matches',
		description: ES
			? 'Descarga un Excel con las coincidencias personalizadas de categorias de vehiculo para esta ejecucion. Util para revisar como se han mapeado las categorias del proveedor a las categorias propias de tu empresa.'
			: 'Downloads an Excel with custom vehicle category matches for this execution. Useful for reviewing how provider categories have been mapped to your companies own categories.',
	},
	// ─── 37: Date column — ACRISS Matches button ────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-acriss"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-acriss"]'),
		icon: 'file-excel',
		title: ES ? 'Descargar Coincidencias ACRISS' : 'Get Custom ACRISS Matches',
		description: ES
			? 'Descarga un Excel con las coincidencias personalizadas de codigos ACRISS para esta ejecucion. Permite verificar como se han clasificado los vehiculos segun el estandar de codigos ACRISS de la industria.'
			: 'Downloads an Excel with custom ACRISS code matches for this execution. Allows verifying how vehicles have been classified according to the industry-standard ACRISS code system.',
	},
	// ─── 38: Date column — Download Multi-Report button ─────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-multireport"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-multireport"]'),
		icon: 'cloud-arrow-down',
		title: ES ? 'Descargar Multi-Informe' : 'Download Multi-Report',
		description: ES
			? 'Genera y descarga el informe Excel completo con todos los precios encontrados en esta ejecucion. Contiene una hoja por cada combinacion de sitio y oficina con todos los datos de precios brutos para su analisis.'
			: 'Generates and downloads the complete Excel report with all prices found in this execution. Contains one sheet per site and office combination with all raw price data for analysis.',
	},
	// ─── 39: Date column — Download Multi-Export button ─────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-multiexport"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-multiexport"]'),
		icon: 'file-arrow-down',
		title: ES ? 'Descargar Multi-Exportacion' : 'Download Multi-Export',
		description: ES
			? 'Descarga el fichero de exportacion personalizado para esta ejecucion, aplicando los filtros configurados en la seccion Export Filters del informe (proveedor, combustible, ACRISS, etc.). Ideal para alimentar sistemas externos o herramientas de analisis propias.'
			: 'Downloads the custom export file for this execution, applying the filters configured in the reports Export Filters section (provider, fuel policy, ACRISS, etc.). Ideal for feeding external systems or proprietary analysis tools.',
	},
	// ─── 40: Row Actions ─────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Actions'),
		icon: 'gears',
		title: ES ? 'Acciones de Fila' : 'Row Actions',
		description: ES
			? 'Acciones individuales para cada informe: Ejecutar (lanza la búsqueda ahora), Editar (modifica la configuración), Clonar (duplica con cambios), Info (ver detalles e historial) y Eliminar. Cuando un informe está en ejecución aparece además el botón «Stop Execution» (icono círculo-stop) que interrumpe la ejecución inmediatamente — a diferencia de «Stop» de la barra de selección, este actúa solo sobre ese informe concreto. Mientras la parada procesa, el botón muestra un spinner.'
			: 'Individual actions for each report: Perform (run searches now), Edit (modify configuration), Clone (duplicate with changes), Info (view details and history), and Delete. When a report is running, an additional "Stop Execution" button (circle-stop icon) appears to immediately interrupt that execution — unlike "Stop" in the selection bar, this acts only on that specific report. While the stop is processing, the button shows a spinner.',
		example: ES
			? 'Ejecutar | Editar | Clonar | Info | Stop Execution (solo si en ejecución) | Eliminar'
			: 'Perform | Edit | Clone | Info | Stop Execution (only while running) | Delete',
	},
	// ─── 41: Show Report Info button ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => (
			document.querySelector('[data-tour-id="tour-show-report-info"]') ||
			document.querySelector('[title="Show Report Info"]')
		),
		// the demo shows the button too; only a real report opens the panel (its steps skip without one)
		skipIf: () => !q('tour-show-report-info'),
		icon: 'circle-info',
		title: ES ? 'Boton Informacion del Informe' : 'Show Report Info Button',
		description: ES
			? 'El boton "i" en la columna Acciones abre el panel de detalles completos del informe. Desde ahi puedes consultar toda la configuracion guardada y el historial de ejecuciones con sus resultados y descargas.'
			: 'The "i" button in the Actions column opens the full report details panel. From there you can view the complete saved configuration and the execution history with its results and downloads.',
		action: () => {
			const firstReport = reportListData.value[0]
			if (firstReport) {
				selectedInfoModalTab.value = INFO_MODAL_TABS.DETAILS
				isOpenInfoModal.value = firstReport.idReport
			}
		},
		waitFor: () => !reportListData.value.length || (!!isOpenInfoModal.value && !!document.querySelector('.MuiDialog-root')),
	},
	// ─── 42: Info Modal — overview ────────────────────────────────────────────────
	{
		id: 'info-start',
		type: 'field',
		// the dialog itself, not its full-screen backdrop
		findTarget: () => document.querySelector('.MuiDialog-root .MuiDialog-paper') || document.querySelector('.MuiDialog-root'),
		skipIf: () => !document.querySelector('.MuiDialog-root'),
		icon: 'rectangle-list',
		title: ES ? 'Panel de Informacion del Informe' : 'Report Info Panel',
		description: ES
			? 'Este modal muestra toda la informacion del informe en dos pestañas: "Details" con la configuracion completa guardada, y "History" con el historial de ejecuciones, sus estados y acciones de descarga. Util para auditar cambios o recuperar resultados anteriores.'
			: 'This modal shows all report information in two tabs: "Details" with the complete saved configuration, and "History" with the execution history, their statuses, and download actions. Useful for auditing changes or retrieving previous results.',
		cleanup: () => { isOpenInfoModal.value = null },
	},
	// ─── 43: Info Modal — Tab Report Details ──────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const dialog = document.querySelector('.MuiDialog-root')
			if (!dialog) return null
			const tabs = dialog.querySelectorAll('[role="tab"]')
			for (const tab of tabs) {
				if (tab.textContent.toLowerCase().includes('details')) return tab
			}
			return null
		},
		skipIf: () => !document.querySelector('.MuiDialog-root'),
		icon: 'memo-circle-info',
		title: ES ? 'Pestaña Details' : 'Details Tab',
		description: ES
			? 'La pestaña "Details" muestra la configuracion completa tal como fue guardada: nombre, sitios, oficinas, moneda, fechas de busqueda, duraciones, horas y dias de ejecucion, emails de notificacion y filtros de exportacion. Sirve para revisar y verificar que todo esta configurado correctamente.'
			: 'The "Details" tab shows the complete configuration as saved: name, sites, offices, currency, search dates, durations, execution hours and days, notification emails, and export filters. Use it to review and verify that everything is configured correctly.',
		action: () => { selectedInfoModalTab.value = INFO_MODAL_TABS.DETAILS },
		cleanup: () => { isOpenInfoModal.value = null },
	},
	// ─── 44: Info Modal — Tab Report History ──────────────────────────────────────
	{
		id: 'info-end',
		type: 'field',
		findTarget: () => {
			const dialog = document.querySelector('.MuiDialog-root')
			if (!dialog) return null
			const tabs = dialog.querySelectorAll('[role="tab"]')
			for (const tab of tabs) {
				if (tab.textContent.toLowerCase().includes('history')) return tab
			}
			return null
		},
		skipIf: () => !document.querySelector('.MuiDialog-root'),
		icon: 'rectangle-history',
		title: ES ? 'Pestaña History' : 'History Tab',
		description: ES
			? 'La pestaña "History" muestra todas las ejecuciones anteriores del informe en orden cronologico inverso. Para cada ejecucion ves la fecha, el estado (Listo, Detenido) y los mismos botones de accion que en la tabla principal: iconos de analizador y botones de descarga. Perfecto para recuperar resultados de ejecuciones pasadas.'
			: 'The "History" tab shows all previous executions of the report in reverse chronological order. For each execution you see the date, the status (Ready, Stopped), and the same action buttons as in the main table: analyzer icons and download buttons. Perfect for retrieving results from past executions.',
		// action: close modal when pressing Next to move to step 44
		// cleanup: safety net if tour is closed while at this step
		action: () => {
			selectedInfoModalTab.value = INFO_MODAL_TABS.HISTORY
			isOpenInfoModal.value = null
		},
		cleanup: () => { isOpenInfoModal.value = null },
	},
	// ─── 45: Enable / Disable toggle ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const sw = document.querySelector('.MuiSwitch-root')
			if (sw) return sw.closest('td') || sw
			return findTableColumn('Off/On') || findTableColumn('On')
		},
		icon: 'toggle-on',
		title: ES ? 'Activar / Desactivar' : 'Enable / Disable',
		description: ES
			? 'Activa o desactiva un informe. Los informes desactivados no se ejecutaran segun su programacion, pero conservan toda la configuracion. Usalo para pausar temporalmente un informe sin eliminarlo.'
			: 'Toggle a report on or off. Disabled reports will not execute on their schedule but retain all configuration. Use this to temporarily pause a report without deleting it.',
	},
	// ─── 46: Multi-Select — Checkboxes ───────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const checkboxes = document.querySelectorAll('th .MuiCheckbox-root, th input[type="checkbox"]')
			if (checkboxes.length) return checkboxes[0].closest('th')
			return null
		},
		icon: 'list-check',
		title: ES ? 'Seleccion Multiple — Casillas' : 'Multi-Select — Checkboxes',
		description: ES
			? 'Marca la casilla de cualquier fila para seleccionar un informe, o usa la casilla de cabecera para seleccionarlos todos. Al seleccionar uno, aparece una barra flotante en la parte inferior de la pantalla con acciones que se aplican a todos los seleccionados.'
			: 'Check the box on any row to select a report, or use the header checkbox to select all. As soon as any row is selected, a floating bar appears at the bottom of the screen with actions that apply to all selected reports.',
		// with real data, tick the first row so the bar appears (the demo always shows it)
		action: () => selectFirstRow(),
		waitFor: () => !!q('tour-multi-btn-edit'),
		cleanup: clearTourSelection,
	},
	// ─── 47: Selection bar — Edit menu ───────────────────────────────────────────
	{
		id: 'bar-start',
		type: 'field',
		findTarget: () => barButton('tour-multi-btn-edit'),
		skipIf: () => !q('tour-multi-btn-edit'),
		icon: 'pen-to-square',
		title: ES ? 'Editar — Multiples Informes' : 'Edit — Multiple Reports',
		description: ES
			? 'El boton «Edit» de la barra abre un menu para cambiar a la vez un mismo dato de todos los informes seleccionados. Pulsa «Next» y lo abrimos para ver cada opcion.'
			: 'The bar\'s "Edit" button opens a menu to change the same setting on all selected reports at once. Press "Next" and we will open it to see each option.',
		action: () => openEditMenu(),
		waitFor: () => isEditMenuOpen(),
	},
	// ─── 48: Edit menu — Sites ───────────────────────────────────────────────────
	{
		id: 'menu-start',
		type: 'field',
		findTarget: () => q('tour-multi-btn-sites'),
		skipIf: () => !q('tour-multi-btn-sites'),
		icon: 'globe-pointer',
		title: ES ? 'Editar Sitios — Multiples Informes' : 'Edit Sites — Multiple Reports',
		description: ES
			? 'Cambia los sitios web monitorizados en todos los informes seleccionados de una sola vez. Abre un modal donde seleccionas los nuevos sitios que se aplicaran a cada informe del grupo. Util para reorganizar mercados o añadir nuevas fuentes de precios a un conjunto de informes.'
			: 'Changes the monitored websites on all selected reports at once. Opens a modal where you select the new sites to apply to every report in the group. Useful for reorganizing markets or adding new price sources to a set of reports.',
		reverseAction: () => closeEditMenu(),
	},
	// ─── 49: Edit menu — Pick-up dates ───────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => q('tour-multi-btn-pickups'),
		skipIf: () => !q('tour-multi-btn-pickups'),
		icon: 'calendars',
		title: ES ? 'Editar Fechas de Recogida — Multiples Informes' : 'Edit Pick-up Dates — Multiple Reports',
		description: ES
			? '«Pick-up dates» cambia las fechas de recogida de todos los informes seleccionados a la vez, sin editarlos uno a uno. Como indica el menu, solo vale para informes con fechas de dias especificos («specific days only»).'
			: '"Pick-up dates" changes the pickup dates of all selected reports at once, without editing them one by one. As the menu says, it only works for reports whose dates are specific days ("specific days only").',
	},
	// ─── 50: Edit menu — Emails ──────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => q('tour-multi-btn-emails'),
		skipIf: () => !q('tour-multi-btn-emails'),
		icon: 'envelopes',
		title: ES ? 'Editar Emails — Multiples Informes' : 'Edit Emails — Multiple Reports',
		description: ES
			? 'Actualiza los destinatarios de email en todos los informes seleccionados a la vez. Cuando cambias el equipo o reasignas informes, puedes actualizar todos los correos en una sola operacion en lugar de abrir cada informe individualmente.'
			: 'Updates the email recipients on all selected reports at once. When your team changes or you reassign reports, you can update all notification addresses in one operation instead of opening each report individually.',
	},
	// ─── 51: Edit menu — Frequency ───────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => q('tour-multi-btn-frequency'),
		skipIf: () => !q('tour-multi-btn-frequency'),
		icon: 'calendar-clock',
		title: ES ? 'Editar Frecuencia — Multiples Informes' : 'Edit Frequency — Multiple Reports',
		description: ES
			? 'Cambia el calendario de ejecucion (dias de la semana y horas) en todos los informes seleccionados de golpe. Util para alinear un grupo de informes a la misma frecuencia, por ejemplo pasar varios informes de ejecucion diaria a solo dias laborables.'
			: 'Changes the execution schedule (days of the week and hours) on all selected reports at once. Useful for aligning a group of reports to the same frequency, e.g. switching several reports from daily execution to weekdays only.',
	},
	// ─── 52: Edit menu — Execution date range (closes the menu) ──────────────────
	{
		id: 'menu-end',
		type: 'field',
		findTarget: () => q('tour-multi-btn-execution-dates'),
		skipIf: () => !q('tour-multi-btn-execution-dates'),
		icon: 'calendar-lines',
		title: ES ? 'Editar Rango de Ejecucion — Multiples Informes' : 'Edit Execution Date Range — Multiple Reports',
		description: ES
			? '«Execution date range» fija para todos los informes seleccionados las fechas entre las que se ejecutan. Util para limitar un grupo de informes a una temporada o campaña concreta.'
			: '"Execution date range" sets, for all selected reports, the dates between which they run. Useful for limiting a group of reports to a specific season or campaign.',
		action: () => closeEditMenu(),
		waitFor: () => !isEditMenuOpen(),
	},
	// ─── 53: Selection bar — Perform ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => barButton('tour-multi-btn-perform'),
		skipIf: () => !q('tour-multi-btn-perform'),
		icon: 'gears',
		title: ES ? 'Ejecutar — Multiples Informes' : 'Perform — Multiple Reports',
		description: ES
			? 'Lanza la busqueda de precios en todos los informes seleccionados de forma inmediata, sin esperar a su proxima ejecucion programada. Solo aparece cuando todos los informes seleccionados estan activados.'
			: 'Immediately triggers a price search on all selected reports, without waiting for their next scheduled run. Only appears when all selected reports are enabled.',
		reverseAction: () => openEditMenu(),
	},
	// ─── 54: Selection bar — Stop (conditional) ──────────────────────────────────
	{
		type: 'field',
		findTarget: () => barButton('tour-multi-btn-stop'),
		skipIf: () => !q('tour-multi-btn-stop'),
		icon: 'circle-stop',
		title: ES ? 'Detener Ejecuciones — Multiples Informes' : 'Stop Executions — Multiple Reports',
		description: ES
			? 'Interrumpe inmediatamente las ejecuciones en curso de todos los informes seleccionados. Este boton solo aparece cuando todos los informes seleccionados estan activados y en ejecucion. La parada es definitiva para ese ciclo — el informe pasara a estado Detenido y no continuara hasta la siguiente ejecucion programada.'
			: 'Immediately interrupts the ongoing executions of all selected reports. This button only appears when all selected reports are enabled and running. The stop is final for that cycle — the report moves to Stopped state and will not continue until the next scheduled run.',
	},
	// ─── 55: Selection bar — Copy ────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => barButton('tour-multi-btn-copy'),
		skipIf: () => !q('tour-multi-btn-copy'),
		icon: 'copy',
		title: ES ? 'Copiar — Multiples Informes' : 'Copy — Multiple Reports',
		description: ES
			? 'Duplica todos los informes seleccionados de una vez. Cada copia conserva la configuracion completa del original (sitios, oficinas, fechas, frecuencia) y se crea con el nombre del original seguido de un sufijo. Util para crear variantes rapidamente a partir de una configuracion base.'
			: 'Duplicates all selected reports at once. Each copy retains the complete configuration of the original (sites, offices, dates, frequency) and is created with the original name followed by a suffix. Useful for quickly creating variants from a base configuration.',
	},
	// ─── 56: Selection bar — Enable ──────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => barButton('tour-multi-btn-enable'),
		skipIf: () => !q('tour-multi-btn-enable'),
		icon: 'toggle-on',
		title: ES ? 'Activar — Multiples Informes' : 'Enable — Multiple Reports',
		description: ES
			? 'Reactiva todos los informes seleccionados: vuelven a ejecutarse automaticamente segun su programacion configurada. Usa este boton para reanudar un grupo de informes que habian sido desactivados previamente.'
			: 'Re-enables all selected reports: they will run automatically again according to their configured schedule. Use this button to resume a group of reports that were previously disabled.',
	},
	// ─── 57: Selection bar — Disable ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => barButton('tour-multi-btn-disable'),
		skipIf: () => !q('tour-multi-btn-disable'),
		icon: 'toggle-off',
		title: ES ? 'Desactivar — Multiples Informes' : 'Disable — Multiple Reports',
		description: ES
			? 'Desactiva todos los informes seleccionados: dejaran de ejecutarse segun su programacion pero conservan toda la configuracion intacta. Es la forma de pausar un conjunto de informes de forma reversible, por ejemplo durante periodos de baja demanda o mantenimiento.'
			: 'Disables all selected reports: they will stop running on their schedule but keep all configuration intact. This is the reversible way to pause a set of reports, for example during low-demand periods or maintenance windows.',
	},
	// ─── 58: Selection bar — Delete ──────────────────────────────────────────────
	{
		id: 'bar-end',
		type: 'field',
		findTarget: () => barButton('tour-multi-btn-delete'),
		skipIf: () => !q('tour-multi-btn-delete'),
		icon: 'trash',
		title: ES ? 'Eliminar — Multiples Informes' : 'Delete — Multiple Reports',
		description: ES
			? 'Elimina permanentemente todos los informes seleccionados tras confirmacion. Esta accion no se puede deshacer — los informes eliminados y su historial de ejecuciones desaparecen definitivamente. Si solo quieres detener temporalmente, usa «Disable» en su lugar.'
			: 'Permanently deletes all selected reports after confirmation. This action cannot be undone — deleted reports and their execution history disappear permanently. If you only want to pause temporarily, use "Disable" instead.',
	},
	// ─── 57: Outro ─────────────────────────────────────────────────────────────
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo Listo!' : "You're All Set!",
		description: ES
			? 'Empieza creando tu primer informe o explorando los existentes. Haz clic en el boton Info de cualquier informe para ver su historial de ejecucion y configuracion detallada. Usa los iconos de analizador en la columna Fecha para ir directamente al analisis de precios.'
			: 'Start by creating your first report or exploring existing ones. Click the Info button on any report to view its execution history and detailed configuration. Use the analyzer icons in the Date column to jump directly to price analysis.',
	},
]
// ─── Modal ranges ─────────────────────────────────────────────────────────────
// Steps that need a drawer, modal, the selection bar or its Edit menu open, found by step id so they
// stay right when steps are added. TourOverlay uses them to restore that state when jumping with dots / Back.
const at = id => TOUR_STEPS.findIndex(s => s.id === id)

export const MODAL_RANGES = [
	// the report creation drawer
	{
		from: at('drawer-start'), to: at('drawer-end'),
		open: () => reportFormDataInnit(),
		close: () => closeDrawer(),
		isOpen: () => isReportDrawerOpen.value === true,
	},
	// the Info Report modal
	{
		from: at('info-start'), to: at('info-end'),
		open: () => {
			const firstReport = reportListData.value[0]
			if (firstReport) {
				selectedInfoModalTab.value = INFO_MODAL_TABS.DETAILS
				isOpenInfoModal.value = firstReport.idReport
			}
		},
		close: () => { isOpenInfoModal.value = null },
		isOpen: () => !!isOpenInfoModal.value && !!document.querySelector('.MuiDialog-root'),
	},
	// the Edit menu of the selection bar (listed before the bar, so it wins inside its steps)
	{
		from: at('menu-start'), to: at('menu-end'),
		open: () => {
			selectFirstRow()
			setTimeout(openEditMenu, 300)
		},
		close: () => closeEditMenu(),
		isOpen: () => isEditMenuOpen(),
	},
	// the floating selection bar (a row must be selected)
	{
		from: at('bar-start'), to: at('bar-end'),
		open: () => selectFirstRow(),
		close: () => clearTourSelection(),
		isOpen: () => !!q('tour-multi-btn-edit'),
	},
]
